import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@cargomatic/ui';
import Store from 'stores/TariffsStore';
import { TariffPropsEditor, validateEffectiveDates } from './TariffPropsEditor';
import { TariffOtherPropsEditor } from './TariffOtherPropsEditor';
import { TariffShippersTable } from './TariffShippersTable';
import { TariffPricesTable } from './TariffPricesTable';
const kDefaultSlug = 'truckload_tariff';
const kDefaultType = 'tabular';
const kBoxToPalletConversionRate = 67;
const kDefaultMarkets = ['LAX'];
function makeBlankTariff() {
    return {
        name: '',
        slug: kDefaultSlug,
        type: kDefaultType,
        markets: kDefaultMarkets,
        zones: [],
        weight_ranges: [],
        load_ranges: [],
        zone_to_weight: [],
        zone_to_load: [],
        minimum_distance: 0,
        minimum_weight: 0,
        minimum_cost: 0,
        maximum_cost: 0,
        cost_per_extra_mile: 0,
        cost_per_extra_lb: 0,
        is_default: false,
        box_to_pallet_conversion_rate: kBoxToPalletConversionRate,
    };
}
export function TariffEditor({ tariff, tariffsList, onChange }) {
    const { t } = useTranslation();
    const draftRef = useRef(tariff || makeBlankTariff());
    const propertiesValidRef = useRef(true);
    const priceMatrixValidRef = useRef(true);
    const mountedRef = useRef(true);
    const [, force] = useState(0);
    // The store subscription re-subscribes on every edit (the `tariff` prop is a
    // fresh object), so on a success emit the dialog's listener can run first and
    // unmount us before our own listener fires from the emit's snapshot. Skip the
    // state updates once unmounted to avoid the "update on unmounted component"
    // leak warning.
    useEffect(() => () => {
        mountedRef.current = false;
    }, []);
    const notify = useCallback((edited) => {
        if (!onChange)
            return;
        const validated = propertiesValidRef.current && priceMatrixValidRef.current;
        onChange(draftRef.current, edited, validated);
    }, [onChange]);
    // Sync from store on save/update success (matches legacy class behavior).
    useEffect(() => {
        const onStoreChange = () => {
            if (!mountedRef.current)
                return;
            if (Store.getStatus() === 'success') {
                const propsTariff = tariff;
                const storeTariff = Store.getTariff();
                if (storeTariff && storeTariff._id) {
                    draftRef.current = Object.assign({}, storeTariff);
                }
                else if (propsTariff) {
                    draftRef.current = Object.assign({}, propsTariff);
                }
                propertiesValidRef.current = true;
                priceMatrixValidRef.current = true;
                force((n) => n + 1);
                notify(false);
            }
            else {
                notify(true);
            }
        };
        Store.addChangeListener(onStoreChange);
        return () => Store.removeChangeListener(onStoreChange);
    }, [tariff, notify]);
    const handlePropertiesChange = useCallback((key, value) => {
        draftRef.current[key] = value;
        propertiesValidRef.current =
            !!(draftRef.current.name && draftRef.current.name.length > 0) && validateEffectiveDates(draftRef.current);
        notify(true);
        force((n) => n + 1);
    }, [notify]);
    const validateZones = (pricesData) => {
        if (!pricesData.zones)
            return true;
        for (let i = 0; i < pricesData.zones.length; ++i) {
            const zone = pricesData.zones[i];
            const ok = i ? zone[0] > 0 && zone[1] >= zone[0] : zone[1] > 0;
            if (!ok)
                return false;
        }
        return true;
    };
    const validatePrices = (pricesData) => {
        if (!pricesData.zone_to_load)
            return true;
        for (const row of pricesData.zone_to_load) {
            for (const price of row) {
                if ((price === null || price === void 0 ? void 0 : price.toFixed(2)) === '0.00') {
                    return false;
                }
            }
        }
        return true;
    };
    const handlePricesDataChange = useCallback((pricesData) => {
        const next = Object.assign(Object.assign({}, draftRef.current), pricesData);
        const pricesValid = validatePrices(next);
        const zonesValid = validateZones(next);
        Object.keys(pricesData).forEach((property) => {
            if (property) {
                draftRef.current[property] =
                    pricesData[property];
            }
        });
        priceMatrixValidRef.current = zonesValid && pricesValid;
        notify(true);
    }, [notify]);
    const hasPersistedTariffId = Boolean(draftRef.current._id);
    const tabs = useMemo(() => [
        { value: 'properties', label: titleize(t('PROPERTIES')) },
        {
            value: 'shippers',
            label: titleize(t('SHIPPER', { count: 2 })),
            disabled: !hasPersistedTariffId,
        },
        { value: 'prices', label: titleize(t('PRICE', { count: 2 })) },
        { value: 'other', label: titleize(t('OTHER')) },
    ], [t, hasPersistedTariffId]);
    return (React.createElement(Tabs, { defaultValue: "properties", className: "w-full" },
        React.createElement(TabsList, null, tabs.map((tab) => (React.createElement(TabsTrigger, { key: tab.value, value: tab.value, disabled: tab.disabled }, tab.label)))),
        React.createElement(TabsContent, { value: "properties", className: "mt-4" },
            React.createElement(TariffPropsEditor, { tariff: draftRef.current, tariffsList: tariffsList, onChange: handlePropertiesChange })),
        React.createElement(TabsContent, { value: "shippers", className: "mt-4" },
            React.createElement(TariffShippersTable, { tariff: draftRef.current })),
        React.createElement(TabsContent, { value: "prices", className: "mt-4" },
            React.createElement(TariffPricesTable, { tariff: draftRef.current, onChange: handlePricesDataChange })),
        React.createElement(TabsContent, { value: "other", className: "mt-4" },
            React.createElement(TariffOtherPropsEditor, { tariff: draftRef.current, onChange: handlePropertiesChange }))));
}
export default TariffEditor;
